<div class="mba hr h-full min-h-0 overflow-y-auto">
  <div class="rp-wrap">

    <!-- Section header -->
    <div class="rp-hd">
      <div class="t">
        <h2>Reporting</h2>
        <span class="mo-sub">
          @if (structureLoading()) {
          <span class="rp-sk w-56"></span>
          } @else if (refreshing()) {
          Refreshing stats…
          } @else if (allRows().length) {
          {{ progress().live }} of {{ progress().total }} posts live · {{ platformNames() }}
          @if (!statsLoading() && lastRefreshed()) {
          · <mba-icon name="refresh-cw" size="size-[13px]" />Updated {{ lastRefreshedText() }}
          }
          } @else {
          Stats for every post on this brief, once creators go live
          }
        </span>
      </div>
      <span class="flex-1"></span>
      @if (allRows().length) {
      <button type="button" class="rp-btn" [class.rp-spin]="refreshing()" [disabled]="refreshing() || !!refreshBlockedUntil()"
        [attr.title]="refreshTooltip()" [attr.aria-label]="refreshing() ? 'Refreshing stats' : refreshTooltip()" (click)="refresh()">
        <mba-icon name="refresh-cw" size="size-4" />{{ refreshing() ? 'Refreshing…' : 'Refresh' }}
      </button>
      <button type="button" class="rp-btn" (click)="exportCsv()"><mba-icon name="download" size="size-4" />Export CSV</button>
      }
    </div>

    @if (structureLoading()) {
    <!-- Loading: skeleton summary and rows -->
    <div class="rp-hero" aria-busy="true" aria-label="Loading report">
      <div class="mo-card">
        <div class="rp-ks">
          @for (t of skeletonTiles; track t) {
          <div class="rp-k"><span class="rp-sk w-20"></span><span class="rp-sk h-7 w-16"></span><span class="rp-sk w-28"></span></div>
          }
        </div>
        <div class="rp-cbox"><span class="rp-sk-block"></span></div>
      </div>
      <div class="rp-side">
        <div class="mo-card rp-card"><span class="rp-sk w-28"></span><span class="rp-sk h-16 w-full"></span></div>
        <div class="mo-card rp-card"><span class="rp-sk w-28"></span><span class="rp-sk h-10 w-full"></span></div>
      </div>
    </div>
    <div class="mo-card flex flex-col gap-4 p-4">
      @for (r of skeletonRows; track r) {
      <div class="flex items-center gap-3">
        <span class="rp-sk h-11 w-8"></span><span class="rp-sk w-40"></span><span class="flex-1"></span><span class="rp-sk w-24"></span><span class="rp-sk w-16"></span>
      </div>
      }
    </div>

    } @else if (structureFailed()) {
    <div class="mo-card mm-emptybox">
      <span class="ic"><mba-icon name="alert-triangle" size="size-[22px]" /></span>
      <b>Couldn’t Load the Report</b>
      <span class="mo-sub">Check your connection and try again.</span>
      <button type="button" class="rp-btn" (click)="retry()">Try again</button>
    </div>

    } @else if (!allRows().length) {
    <!-- No posts yet -->
    <div class="mo-card mm-emptybox">
      <span class="ic"><mba-icon name="bar-chart-3" size="size-[22px]" /></span>
      <b>No Posts Live Yet</b>
      <span class="mo-sub">
        Stats show up here once creators post.
        @if (progress().total) { {{ progress().total }} post{{ progress().total === 1 ? ' is' : 's are' }} due across {{ platformNames() }}. }
      </span>
      <button type="button" class="rp-btn" (click)="seeWhoIsPosting()"><mba-icon name="users" size="size-4" />See who’s posting</button>
    </div>

    } @else {

    @if (earlyNumbers()) {
    <div class="mo-card rp-early" role="note">
      <span class="ic"><mba-icon name="hourglass" size="size-[18px]" /></span>
      <div><b>Early Numbers</b><span class="mo-sub">Posts keep picking up views for 7–10 days after they go live, so expect these to grow.</span></div>
    </div>
    }

    @if (sourceNotice(); as notice) {
    <div class="rp-notice" role="status"><mba-icon name="info" size="size-4" />{{ notice }}</div>
    }
    @if (statsFailed()) {
    <div class="rp-notice err" role="alert">
      <mba-icon name="alert-triangle" size="size-4" />Couldn’t load stats.
      <button type="button" class="rp-link" (click)="retry()">Try again</button>
    </div>
    }

    @if (prefs().collapsed) {
    <!-- Collapsed summary -->
    <div class="mo-card rp-mini">
      <div><span class="mo-k">Views</span>@if (statsLoading()) {<span class="rp-sk h-6 w-14"></span>} @else {<b>{{ tiles()[0].value }}</b>}</div>
      <div><span class="mo-k">Engagement rate</span>@if (statsLoading()) {<span class="rp-sk h-6 w-14"></span>} @else {<b>{{ percent(summaryTotals().er) }}</b>}</div>
      @if (canSeeCost()) {
      <div><span class="mo-k">Cost per 1K views</span>@if (statsLoading()) {<span class="rp-sk h-6 w-14"></span>} @else {<b>{{ tiles()[tiles().length - 1].value }}</b>}</div>
      }
      <div><span class="mo-k">Posts live</span><b>{{ progress().live }}<span class="text-[17px] text-(--fg-muted)">/{{ progress().total }}</span></b></div>
      <span class="mm-sp"></span>
      <button type="button" class="rp-btn sm" (click)="toggleCollapsed()"><mba-icon name="chevron-down" size="size-4" />Show summary</button>
    </div>
    } @else {
    <!-- Summary -->
    <div class="rp-hero">
      <div class="mo-card mm-chartcard">
        <div class="rp-ks" role="tablist" aria-label="Metric">
          @for (t of tiles(); track t.key) {
          <button type="button" role="tab" class="rp-k" [class.on]="metric() === t.key" [attr.aria-selected]="metric() === t.key"
            (click)="setMetric(t.key)">
            <span class="mo-k"><mba-icon [name]="t.icon" size="size-[13px]" />{{ t.label }}</span>
            @if (statsLoading()) {<span class="rp-sk h-7 w-16"></span>} @else {<span class="v">{{ t.value }}</span>}
            @if (statsLoading() || (t.sub === null && seriesLoading())) {
            <span class="rp-sk w-28"></span>
            } @else if (t.sub) {
            <span class="d">{{ t.sub }}</span>
            }
          </button>
          }
        </div>
        <div class="rp-ch">
          <h3 class="bb-h">{{ metricLabel(metric()) }} Over Time</h3>
          @if (activePlatform(); as ap) {
          <span class="rp-tag"><img [src]="logos[ap]" alt="" class="size-3 object-contain" />{{ platformLabel[ap] }} only
            <button type="button" aria-label="Clear platform filter" (click)="togglePlatform('all')"><mba-icon name="x" size="size-3" /></button>
          </span>
          }
          <span class="mm-sp"></span>
          <div class="mm-seg" role="group" aria-label="Chart range">
            <button type="button" [class.on]="prefs().range === '7'" [attr.aria-pressed]="prefs().range === '7'" (click)="setRange('7')">Last 7 days</button>
            <button type="button" [class.on]="prefs().range === 'all'" [attr.aria-pressed]="prefs().range === 'all'" (click)="setRange('all')">All time</button>
          </div>
          <button type="button" class="rp-ib" aria-label="Hide summary" title="Hide summary" (click)="toggleCollapsed()">
            <mba-icon name="chevron-up" size="size-4" />
          </button>
        </div>
        @if (statsLoading() || seriesLoading()) {
        <div class="rp-cbox"><span class="rp-sk-block" aria-label="Loading chart"></span></div>
        } @else {
        <hr-chart [points]="points()" [metric]="metric()" [currencyCode]="currency()" [pinned]="pinned()" [now]="now()" />
        }
        <div class="rp-cf">
          <span class="lg mo-sub"><i></i>A post went live</span>
          <span class="mo-sub">Running total since the brief started{{ openRow() ? ' · dashed line marks the post you’re viewing' : '' }}</span>
        </div>
      </div>

      <div class="rp-side">
        <div class="mo-card rp-card">
          <div class="h"><h3 class="bb-h">By Platform</h3>@if (byPlatform().length > 1) {<span class="mo-sub">Tap to filter</span>}</div>
          @for (p of byPlatform(); track p.platform) {
          <button type="button" class="rp-pb" [class.on]="platform() === p.platform" [attr.aria-pressed]="platform() === p.platform"
            [disabled]="byPlatform().length < 2" (click)="togglePlatform(p.platform)">
            <span class="nm"><img [src]="logos[p.platform]" alt="" class="size-4 object-contain" />{{ platformLabel[p.platform] }}</span>
            @if (statsLoading()) {<span class="rp-sk w-12"></span>} @else {<span class="vv">{{ formatMetric('views', p.views, currency()) }}</span>}
            <span class="tr"><i [class.k]="p.platform === 'tiktok'" [style.width.%]="statsLoading() ? 0 : p.width"></i></span>
            <span class="mo-sub">{{ p.n }} post{{ p.n === 1 ? '' : 's' }}@if (!statsLoading()) { · {{ percent(p.er) }} engagement · {{ formatMetric('engagements', p.engagements, currency()) }} engagements}</span>
          </button>
          }
        </div>
        <div class="mo-card rp-card">
          <div class="h"><h3 class="bb-h">Posting Progress</h3></div>
          <span class="rp-big">{{ progress().live }}<small>/{{ progress().total }}</small></span>
          <span class="mo-sub -mt-1">posts live</span>
          <div class="rp-sb" role="img" [attr.aria-label]="progress().live + ' live, ' + progress().waiting + ' not posted yet'">
            <i class="lv" [style.width.%]="progress().livePct"></i><i class="du" [style.width.%]="progress().waitingPct"></i>
          </div>
          <div class="rp-lg">
            <span><i class="bg-(--app-accent)"></i>{{ progress().live }} live</span>
            <span><i class="bg-[#E6E6E6]"></i>{{ progress().waiting }} not posted</span>
          </div>
          @if (progress().waiting) {
          <button type="button" class="rp-btn sm" (click)="toggleNotPosted()">
            <mba-icon [name]="prefs().showNotPosted ? 'eye-off' : 'list-plus'" size="size-4" />
            {{ prefs().showNotPosted ? 'Hide' : 'Show ' + progress().waiting + ' not posted in table' }}
          </button>
          }
        </div>
      </div>
    </div>
    }

    <!-- Toolbar -->
    <div class="rp-tb">
      <span class="ttl">{{ tableRows().length }} Post{{ tableRows().length === 1 ? '' : 's' }}</span>
      <span class="mo-sub">Sorted by {{ sortCaption() }}</span>
      @if (activePlatform(); as ap) {
      <span class="rp-tag"><img [src]="logos[ap]" alt="" class="size-3 object-contain" />{{ platformLabel[ap] }}
        <button type="button" aria-label="Clear platform filter" (click)="togglePlatform('all')"><mba-icon name="x" size="size-3" /></button>
      </span>
      }
      @if (prefs().showNotPosted) {
      <span class="rp-tag">Showing not posted
        <button type="button" aria-label="Hide not posted" (click)="toggleNotPosted()"><mba-icon name="x" size="size-3" /></button>
      </span>
      }
      <span class="mm-sp"></span>
      <label class="mm-search">
        <mba-icon name="search" size="size-4" />
        <input type="search" placeholder="Search creators" aria-label="Search creators" [value]="query()" (input)="onSearch($event)" />
      </label>
    </div>

    <hr-posts-table
      [rows]="tableRows()" [totals]="tableTotals()" [totalsLabel]="totalsLabel()" [sort]="prefs().sort" [hot]="metric()"
      [notPosted]="notPostedVisible()" [creatorNames]="creatorNames()" [currentId]="openId()" [statsLoading]="statsLoading()"
      [now]="now()" [nudgedUntil]="nudgedUntil()" [nudging]="nudging()" [filtered]="filtered()"
      (open)="openPost($event)" (sortBy)="setSort($event)" (nudge)="nudge($event)" (clearFilters)="clearFilters()"
      (copyAdCode)="copyAdCode($event)" />
    }
  </div>

  @if (openRow(); as row) {
  <hr-post-drawer [row]="row" [index]="openIndex()" [count]="tableRows().length" [series]="timeseriesData()?.posts?.[row.post.id] ?? null"
    [seriesLoading]="seriesLoading()" [statsLoading]="statsLoading()" [briefEr]="briefEr()" [now]="now()"
    (close)="openId.set(null)" (step)="step($event)" />
  }
</div>
